import { useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './header.js';
import { Sidebar } from './sidebar.js';
import { ThemeProvider } from './theme-provider.js';
import { PwaPrompt } from './pwa-prompt.js';
import { useMediaQuery } from '../hooks/use-media-query.js';
import { useServerApp } from '../hooks/use-server-app.js';
import { useAppBadge } from '../hooks/use-app-badge.js';
import { PullIndicator, usePullToRefresh } from './pull-to-refresh.js';

const LANDSCAPE_QUERY = '(min-width: 1024px), (min-width: 768px) and (orientation: landscape)';

export function Layout() {
  const useSidebar = useMediaQuery(LANDSCAPE_QUERY);
  // A server's pages are that server's own installable app (one PWA per client)
  useServerApp();
  // The decisions waiting, on the app icon (#938)
  useAppBadge();
  const main = useRef<HTMLElement>(null);
  const pull = usePullToRefresh(main);

  return (
    <ThemeProvider>
      {/* Only the content scrolls: anything taller than the screen (a popover, a dialog) must not move the whole page,
          sidebar included */}
      <div className={`layout-bg flex h-screen overflow-hidden ${useSidebar ? 'flex-row' : 'flex-col'}`}>
        {useSidebar ? <Sidebar /> : <Header />}
        <main ref={main} className="layout-main relative min-h-0 flex-1 overflow-y-auto">
          <PullIndicator state={pull.state} distance={pull.distance} />
          {/* The content follows the finger while pulling, and settles back when released */}
          <div className={`h-full ${pull.state === 'pulling' || pull.state === 'armed' ? '' : 'ptr-settle'}`} style={pull.distance ? { transform: `translateY(${pull.distance}px)` } : undefined}>
            <Outlet />
          </div>
        </main>
        <PwaPrompt />
      </div>
    </ThemeProvider>
  );
}
